import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Grid, Card, CardContent, Typography, Box, Button, Avatar, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Chip, Stack, Divider, Switch, FormControlLabel, CircularProgress } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import { toast } from 'react-toastify';
import StatusChip from '../../components/StatusChip';
import { formatCurrency, formatDate, formatDateTime, formatPhone } from '../../utils/formatters';
import { getUserDetail, updateUserStatus, getUserTransactions } from '../../api/endpoints/users';

const UserDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, [id]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [userRes, txnRes] = await Promise.all([
        getUserDetail(id),
        getUserTransactions(id, { limit: 10 }),
      ]);
      setUser(userRes.data.data);
      setTransactions(txnRes.data.data || []);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to load user details');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleStatus = async () => {
    try {
      await updateUserStatus(id, !user.isActive);
      setUser((prev) => ({ ...prev, isActive: !prev.isActive }));
      toast.success(`User ${user.isActive ? 'deactivated' : 'activated'} successfully`);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to update user status');
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!user) {
    return <Typography>User not found</Typography>;
  }

  return (
    <Box>
      <Button startIcon={<ArrowBackIcon sx={{ fontSize: 28 }} />} onClick={() => navigate('/users')} sx={{ mb: 2 }}>
        Back to Users
      </Button>

      <Grid container spacing={3}>
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <Avatar sx={{ width: 80, height: 80, mx: 'auto', mb: 2, bgcolor: 'primary.main', fontSize: 32, fontWeight: 700 }}>
                {user.name.split(' ').map(n => n[0]).join('')}
              </Avatar>
              <Typography variant="h6">{user.name}</Typography>
              <StatusChip status={user.isActive ? 'active' : 'inactive'} sx={{ mt: 1 }} />
              <Divider sx={{ my: 2 }} />
              <Stack spacing={1.5} alignItems="flex-start">
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <PhoneIcon fontSize="small" color="action" />
                  <Typography variant="body2">{formatPhone(user.phone)}</Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <EmailIcon fontSize="small" color="action" />
                  <Typography variant="body2">{user.email}</Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CalendarTodayIcon fontSize="small" color="action" />
                  <Typography variant="body2">Joined {formatDate(user.createdAt)}</Typography>
                </Box>
              </Stack>
              <Divider sx={{ my: 2 }} />
              <FormControlLabel
                control={<Switch checked={user.isActive} onChange={handleToggleStatus} />}
                label={user.isActive ? 'Active' : 'Inactive'}
              />
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={8}>
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={6} sm={3}>
              <Card><CardContent sx={{ textAlign: 'center', py: 2 }}>
                <Typography variant="h5" fontWeight={700} sx={{ color: 'primary.main' }}>{formatCurrency(user.walletBalance)}</Typography>
                <Typography variant="caption" color="text.secondary">Wallet Balance</Typography>
              </CardContent></Card>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Card><CardContent sx={{ textAlign: 'center', py: 2 }}>
                <Typography variant="h5" fontWeight={700} sx={{ color: 'secondary.main' }}>{formatCurrency(user.totalSpent || 0)}</Typography>
                <Typography variant="caption" color="text.secondary">Total Spent</Typography>
              </CardContent></Card>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Card><CardContent sx={{ textAlign: 'center', py: 2 }}>
                <Typography variant="h5" fontWeight={700}>{user.totalOrders || 0}</Typography>
                <Typography variant="caption" color="text.secondary">Orders</Typography>
              </CardContent></Card>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Card><CardContent sx={{ textAlign: 'center', py: 2 }}>
                <Typography variant="h5" fontWeight={700}>{user.totalChats || 0}</Typography>
                <Typography variant="caption" color="text.secondary">Chat Sessions</Typography>
              </CardContent></Card>
            </Grid>
          </Grid>

          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 2 }}>Recent Transactions</Typography>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>ID</TableCell>
                      <TableCell>Type</TableCell>
                      <TableCell>Category</TableCell>
                      <TableCell align="right">Amount</TableCell>
                      <TableCell align="right">Balance</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Date</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {transactions.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={7} align="center">No transactions found</TableCell>
                      </TableRow>
                    ) : transactions.map((txn) => (
                      <TableRow key={txn._id || txn.id} hover>
                        <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>{txn._id?.slice(-8) || txn.id}</TableCell>
                        <TableCell>
                          <Chip size="small" label={txn.type} color={txn.type === 'credit' ? 'success' : 'error'} variant="outlined" />
                        </TableCell>
                        <TableCell>{txn.category}</TableCell>
                        <TableCell align="right">{formatCurrency(txn.amount)}</TableCell>
                        <TableCell align="right">{formatCurrency(txn.balance || 0)}</TableCell>
                        <TableCell><StatusChip status={txn.status} /></TableCell>
                        <TableCell>{formatDate(txn.createdAt)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default UserDetail;